import RelatedTopicsSection from "../components/RelatedTopicsSection";
import { checkGreenIcon, EUIcon } from "../assets/imageAssets";
import { ArrowDown } from "@untitledui/icons/ArrowDown";
import { Lock02 } from "@untitledui/icons/Lock02";
import { LockUnlocked03 } from "@untitledui/icons/LockUnlocked03";
import { Download01 } from "@untitledui/icons/Download01";
import BarChart from "../components/BarChart";
import LollipopChart from "../components/LollipopChart";
import Button from "../components/Buttons/button";
import DataTable from "../components/DataTable";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { use, useEffect, useMemo, useState } from "react";
import { profilesIdTopics_req, userActivity_req } from "../api/profiles.api";
import { getAuthToken } from "../utils/authToken";
import { slugify } from "../utils/slugify";

// Mock chart items used while the live payload was still unconfirmed. The
// backend now always sends `items: { chart: [], table: [] }`, so this is
// kept only for reference and is never imported/used - remove entirely once
// the live shape has proven stable.
// const MOCK_BAR_CHART_ROWS = [
//   { label: "Latvia", value: 42, is_user: false },
//   { label: "Poland", value: 45, is_user: false },
//   { label: "Slovenia", value: 48, is_user: false },
//   { label: "Slovakia", value: 51, is_user: false },
//   { label: "Germany", value: 55, is_user: false },
//   { label: "Czechia", value: 58, is_user: false },
//   { label: "Belgium", value: 61, is_user: false },
//   { label: "Switzerland", value: 63, is_user: false },
//   { label: "You", value: 65, is_user: true },
//   { label: "Albania", value: 68, is_user: false },
//   { label: "Bulgaria", value: 72, is_user: false },
//   { label: "Lithuania", value: 76, is_user: false },
//   { label: "Portugal", value: 81, is_user: false },
//   { label: "Cyprus", value: 84, is_user: false },
//   { label: "European Union - 27 countries (from 2020)", value: 88, is_user: false },
//   { label: "Euro area - 20 countries (from 2023)", value: 89, is_user: false },
//   { label: "Ireland", value: 93, is_user: false },
//   { label: "Austria", value: 97, is_user: false },
//   { label: "Italy", value: 112, is_user: false },
// ];
//
// const MOCK_LOLLIPOP_CHART_ROWS = [
//   { label: "Your Value", value: 65, is_user: true, legend_label: "Your Data" },
//   { label: "European Avg", value: 99, is_user: false, legend_label: "European Countries" },
//   { label: "Bulgarian Avg", value: 87, is_user: false, legend_label: "Your Country" },
// ];
//
// const MOCK_CHART_ITEMS = [
//   {
//     id: "mock-chart-bar",
//     type: "bar",
//     order: 1,
//     x_axis_label: "2025",
//     legend: { user_answer_label: "Your Data", system_data_label: "Our Data" },
//     rows: MOCK_BAR_CHART_ROWS,
//   },
//   {
//     id: "mock-chart-lollipop",
//     type: "lollipop",
//     order: 2,
//     x_axis_label: "2025",
//     rows: MOCK_LOLLIPOP_CHART_ROWS,
//   },
// ];

// Backend titles occasionally carry stray leading/trailing whitespace
// (e.g. "Compare Your Height with the National Average "); trim everywhere
// a title is displayed instead of relying on the payload being clean.
const trimTitle = value => (typeof value === "string" ? value.trim() : value);

// Only these two are real chart kinds today; anything else is skipped
// rather than defaulting to a bar chart.
const KNOWN_CHART_TYPES = ["bar", "lollipop"];

const Report = ({ seconds }) => {
  const navigation = useNavigate();
  const sessionId = useSelector(state => state.me.sessionData);
  const location = useLocation();
  const [dataResponse, setDataResponse] = useState("");

  const params = new URLSearchParams(location.search);
  const data = location?.state?.data;
  const reportTopic = data?.topic;
  // Live payload shape: `data.items = { chart: [...], table: [...] }`.
  // Charts and tables are independent -
  // admin-managed, may or may not share ids/topics - so neither list falls
  // back to the other.
  const tables = useMemo(() => {
    const list = Array.isArray(data?.items?.table) ? data.items.table : [];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [data]);
  // No mock fallback: an admin-managed report can legitimately have zero
  // charts, and the page must render (tables only) without crashing.
  const charts = useMemo(() => {
    const list = Array.isArray(data?.items?.chart) ? data.items.chart : [];
    return list.filter(item => KNOWN_CHART_TYPES.includes(item?.type)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [data]);
  const user = useSelector(state => state.me?.data);
  const topic = params.get("topic");
  const profileIdUrl = location.pathname?.split("/").pop();

  const [dataTopic, setDataTopic] = useState(null);

  const downloadFile = async () => {
    try {
      await userActivity_req(sessionId, seconds, "work_pay_explorer");
      const profileId = localStorage.getItem("profileId");
      const topicId = localStorage.getItem("topicId");
      const token = getAuthToken();

      fetch(`https://backtoreality-api.codebnb.me/api/v1/profiles/${profileId}/topics/${topicId}/download_pdf`, {
        method: "POST",
        headers: {
          Authorization: token,
        },
      })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${slugify(reportTopic?.title, "report-summary")}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(error => console.error("Error fetching PDF:", error));

      localStorage.removeItem("profileId");
      localStorage.removeItem("topicId");
    } catch (e) {}
  };
  const getProfileTopics = async () => {
    const profileId = localStorage.getItem("profileId");
    const response = await profilesIdTopics_req(profileId);

    setDataResponse(response?.response);
    const topicsData = response?.result?.topics?.filter(item => item.id !== Number(topic));
    setDataTopic(topicsData);
  };
  useEffect(() => {
    getProfileTopics();
  }, []);
  const questionScreen = id => {
    const topicItem = dataTopic?.find(t => t.id === id);
    if (topicItem?.is_question) {
      navigation(`/profiles/${profileIdUrl}/topics/${id}/questions`);
    } else {
      navigation(`/profiles/${profileIdUrl}/topics/${id}`);
    }
  };
  console.log("mainData", data);
  console.log("TOPICS", dataTopic);
  return (
    <div className="report-page">
      <div className="container">
        <div className="compare-top text-center">
          <div className="content">
            {/* <img src={EUIcon} alt={""} /> */}
            <h3>{reportTopic?.title || "Survey Results"}</h3>
            <p className="text-color-grey mb-24 compare-top__description">
              Based on your responses to the <strong>{reportTopic?.title || "survey"}</strong> questions, we have prepared a personalized report that brings together the most relevant insights and observations.
            </p>
            {user?.id ? <Button text={"Download PDF"} className={"center"} onClick={downloadFile} icon={<Download01 size={20} color={"#fff"} className={"mr-8"} />} /> : <Button text={"Unlock Full Report & PDF Download"} className={"center"} icon={<LockUnlocked03 size={20} color={"#fff"} className={"mr-8"} />} />}
          </div>
        </div>
        <div className="compare-average">
          {/* /////Old dead code///// */}
          {/* <div className="compare-average__top flex flex-align-center flex-justify-center">
            <div className="text-center">
              <p>Your Average</p>
              <span>
                € <b className={`${user?.id ? "" : "blur"}`}>10.5</b>
              </span>
            </div>
            <div className="text-center">
              <p>European Average</p>
              <span>
                € <b className={`${user?.id ? "" : "blur"}`}>40.2</b>
              </span>
            </div>
          </div> */}
          {/* <div className="compare-average__bottom text-center">
            <p className="flex flex-align-center flex-justify-center mb-24">
              <span className="flex flex-align-center mr-4">
                <ArrowDown color={"#12B76A"} size={20} />
                10%
              </span>
              Your rate [X] is [higher] than the average rate in EU [Y].
              {data?.response}
            </p>
            <button className="unlock-button flex flex-align-center">
              <span className="flex flex-align-center flex-justify-center">
                <Lock02 size={12} color={"#9E77ED"} />
              </span>
              Unlock Now
            </button>
          </div> */}
          {/* /////END Old dead code ///// */}
        </div>
        <div className="compare-charts">
          <h3 className="mb-12">Visual Comparison</h3>
          <p className="mb-32">This report compares your responses with the existing data and highlights key similarities, differences, and trends.</p>
          {charts.map(item => {
            // Each chart's own title (if present) drives its Y-axis label;
            // otherwise fall back to the report's topic title.
            const yAxisTitle = trimTitle(item.title) || trimTitle(reportTopic?.title);

            return item.type === "lollipop" ? <LollipopChart key={item.id} item={item} yAxisTitle={yAxisTitle} /> : <BarChart key={item.id} item={item} yAxisTitle={yAxisTitle} />;
          })}
          {tables.map(item => (
            <DataTable key={item.id} title={trimTitle(item.title)} columns={item.columns} rows={item.rows} />
          ))}
          {user?.id ? (
            <Button text={"Download PDF"} className={"center"} onClick={downloadFile} icon={<Download01 size={20} color={"#fff"} className={"mr-8"} />} />
          ) : (
            <div className="unlock-report-box">
              <div className="text-center">
                <div className="lock-icon">
                  <Lock02 size={24} color={"#7F56D9"} />
                </div>
                <h3 className="mb-12">Unlock Your Full Report</h3>
                <p className="text-color-grey mb-32">
                  Your full report includes chart values, personalized explanations, and a downloadable PDF. <br /> Get the full picture — no guesswork, no hidden details.
                </p>
                <ul className="flex flex-align-center flex-justify-center mb-32">
                  <li className="flex flex-align-center">
                    <img src={checkGreenIcon} alt="" />
                    See exactly where you stand
                  </li>
                  <li className="flex flex-align-center">
                    <img src={checkGreenIcon} alt="" />
                    Download and share
                  </li>
                  <li className="flex flex-align-center">
                    <img src={checkGreenIcon} alt="" />
                    Understand what drives your outcome
                  </li>
                </ul>
                <Button text={"Unlock Full Report & PDF Download"} className={"center"} icon={<LockUnlocked03 size={20} color={"#fff"} className={"mr-8"} />} />
              </div>
            </div>
          )}
        </div>
      </div>
      <RelatedTopicsSection title={"Explore other related topics"} topicsList={dataTopic} color={"purple"} questionScreen={questionScreen} />
    </div>
  );
};

export default Report;
